import { useContext, useMemo } from 'react';

import {
  BookmarkIcon,
  LockClosedIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { routeToFeatureMap } from 'apps/web/constants/featureMap';
import { nonCurriculumRoutes } from 'apps/web/constants/routes';
import CreditContext from 'apps/web/context/credits/context';
import useStorePinnedApps from 'apps/web/hooks/useStorePinnedApps';
import { useTooltip } from 'apps/web/hooks/useTooltip';
import {
  isFeatureMenuItem,
  isPinnedAppMenuItem,
  MenuItemType,
} from 'apps/web/types/menu-item';
import { getFeaturePath } from 'apps/web/utils/pathUtils';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';

import { Button } from '../Button';
import PremiumIcon from '../PremiumIcon';

export default function SidebarMenuItem({
  item,
  shouldHighlight,
  isPinned,
  onNavigate,
}: {
  item: MenuItemType;
  shouldHighlight: boolean;
  isPinned: boolean;
  onNavigate?: (href: string) => void;
}) {
  const { context: creditContext } = useContext(CreditContext);
  const { userCredit } = creditContext;
  const userHasPremium = userCredit.paidCredits > 0;
  const { togglePinnedApp } = useStorePinnedApps();

  const tooltipContent = useMemo(
    () => (isPinned ? 'Remove from Favorites' : 'Add to Favorites'),
    [isPinned],
  );

  const tooltip = useTooltip<HTMLButtonElement>({ content: tooltipContent });

  function handlePinClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    e.preventDefault();
    tooltip.hide();
    togglePinnedApp(item.href);
    const button = e.currentTarget as HTMLButtonElement;
    if (button) {
      button.blur();
    }
  }

  function handleNavigation(e: React.MouseEvent<HTMLAnchorElement>) {
    if (onNavigate && item.href) {
      e.preventDefault();
      onNavigate(item.href);
    }
  }

  function handleStarHover(entering: boolean, e: React.MouseEvent) {
    const link = e.currentTarget.closest('a');
    if (entering) {
      link?.removeAttribute('title');
      tooltip.show();
    } else {
      link?.setAttribute('title', item.name);
      tooltip.hide();
    }
  }

  const featurePath = getFeaturePath(item.href);
  const feature = routeToFeatureMap[featurePath];
  const isNonCurriculum =
    feature &&
    nonCurriculumRoutes.some((route) => route.featureKey === feature);

  return (
    <>
      <li
        key={item.name}
        className={clsx(
          shouldHighlight
            ? 'border-blue-600'
            : 'border-transparent text-gray-500 hover:border-l-4 hover:border-blue-500',
          'group flex justify-between border-l-4 pl-4 duration-200 hover:bg-gray-100 hover:pl-6',
        )}
      >
        <Link
          href={item.href}
          title={item.name}
          className={clsx(
            'text-md group relative flex min-h-[40px] w-full items-center gap-x-3 p-2 font-semibold leading-6 transition-all duration-100',
            shouldHighlight && 'text-blue-600',
          )}
          onClick={handleNavigation}
        >
          <div className="flex w-full items-center gap-4 pr-8">
            {isPinnedAppMenuItem(item) && (
              <div className="flex-shrink-0 rounded-full">
                {isNonCurriculum ? (
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-50">
                    <BookmarkIcon className="h-4 w-4 text-blue-500" />
                  </div>
                ) : (
                  <Image
                    alt={item.name}
                    className="h-5 w-auto max-w-[24px] object-contain"
                    height={20}
                    src={item.logo}
                    width={24}
                  />
                )}
              </div>
            )}
            <div className="relative flex min-w-0 flex-1 flex-row items-center justify-start gap-2">
              <span className="w-fit break-words">{item.name}</span>
              {isFeatureMenuItem(item) && (
                <>
                  {item.isPremium && (
                    <>
                      {userHasPremium ? (
                        <PremiumIcon className="inline h-5 fill-[#FFB743]" />
                      ) : (
                        <span className="flex max-h-6 px-2 py-1 text-sm text-[#FFB800]">
                          <LockClosedIcon className="h-5 w-6" />
                        </span>
                      )}
                    </>
                  )}
                  {item.isNew && (
                    <span className="beta-pill z-10 max-h-6 rounded-2xl px-2 text-[10px] font-bold text-white">
                      New
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          <Button
            ref={tooltip.triggerRef}
            aria-describedby="favorites-tooltip"
            aria-label={tooltipContent}
            className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-gray-500 opacity-20 hover:text-blue-600 focus:opacity-100 focus:outline-none group-hover:opacity-100 sm:opacity-0"
            onClick={handlePinClick}
            onMouseEnter={(e) => handleStarHover(true, e)}
            onMouseLeave={(e) => handleStarHover(false, e)}
          >
            {isPinned ? (
              <StarIconSolid className="text-blue-400" />
            ) : (
              <StarIcon className="hover:text-blue-400" />
            )}
          </Button>
        </Link>
      </li>
      {tooltip.element}
    </>
  );
}
